import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
  NgZone,
  AfterViewInit,
  OnDestroy
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { environment } from '../../../environments/environment';

declare const grecaptcha: any;
declare const hcaptcha: any;
declare const turnstile: any;

@Component({
  selector: 'app-captcha-wrapper',
  templateUrl: './captcha-wrapper.component.html',
  styleUrls: ['./captcha-wrapper.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CaptchaWrapperComponent),
      multi: true
    }
  ]
})
export class CaptchaWrapperComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {
  @Input() siteKey?: string;
  @Input() languageCode?: string;
  @Input() theme?: 'light' | 'dark' = 'light';
  @Input() size?: 'normal' | 'compact' = 'normal';
  @Output() verify = new EventEmitter<string>();
  @Output() expired = new EventEmitter<any>();
  @Output() error = new EventEmitter<any>();

  @ViewChild('recaptchaContainer', { static: false }) recaptchaContainer?: ElementRef;
  @ViewChild('hcaptchaContainer', { static: false }) hcaptchaContainer?: ElementRef;
  @ViewChild('turnstileContainer', { static: false }) turnstileContainer?: ElementRef;

  captchaType: 'recaptcha' | 'hcaptcha' | 'turnstile' = 'recaptcha';
  recaptchaSiteKey: string;
  hcaptchaSiteKey: string;
  turnstileSiteKey: string;
  token: string | undefined;
  
  private recaptchaWidgetId: number | null = null;
  private hcaptchaWidgetId: string | null = null;
  private turnstileWidgetId: string | null = null;
  private scriptLoaded = false;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private ngZone: NgZone) {}

  ngOnInit(): void {
    // Define o tipo de captcha baseado no environment
    this.captchaType = environment.captchaType as 'recaptcha' | 'hcaptcha' | 'turnstile';
    
    this.recaptchaSiteKey = this.siteKey || environment.recaptcha.siteKey;
    this.hcaptchaSiteKey = this.siteKey || environment.hcaptcha?.siteKey || '';
    this.turnstileSiteKey = this.siteKey || environment.turnstile?.siteKey || '';

    if (this.captchaType === 'recaptcha') {
      this.loadRecaptchaScript();
    } else if (this.captchaType === 'hcaptcha') {
      this.loadHcaptchaScript();
    } else if (this.captchaType === 'turnstile') {
      this.loadTurnstileScript();
    }
  }

  ngAfterViewInit(): void {
    if (this.captchaType === 'recaptcha' && this.scriptLoaded) {
      this.renderRecaptcha();
    } else if (this.captchaType === 'hcaptcha' && this.scriptLoaded) {
      this.renderHcaptcha();
    } else if (this.captchaType === 'turnstile' && this.scriptLoaded) {
      this.renderTurnstile();
    }
  }

  ngOnDestroy(): void {
    if (this.recaptchaWidgetId !== null && typeof grecaptcha !== 'undefined') {
      try {
        grecaptcha.reset(this.recaptchaWidgetId);
      } catch (e) {
        console.error('Erro ao remover reCAPTCHA:', e);
      }
    }

    if (this.hcaptchaWidgetId && typeof hcaptcha !== 'undefined') {
      try {
        hcaptcha.remove(this.hcaptchaWidgetId);
      } catch (e) {
        console.error('Erro ao remover hCaptcha:', e);
      }
    }

    if (this.turnstileWidgetId && typeof turnstile !== 'undefined') {
      try {
        turnstile.remove(this.turnstileWidgetId);
      } catch (e) {
        console.error('Erro ao remover Turnstile:', e);
      }
    }
  }

  private loadRecaptchaScript(): void {
    if (typeof grecaptcha !== 'undefined' && grecaptcha.render) {
      this.scriptLoaded = true;
      return;
    }

    if (document.querySelector('script[src*="recaptcha/api.js"]')) {
      this.waitForRecaptcha();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=explicit`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.waitForRecaptcha();
    };
    script.onerror = () => {
      console.error('Falha ao carregar o script do reCAPTCHA');
      this.error.emit('Falha ao carregar reCAPTCHA');
    };
    document.head.appendChild(script);
  }

  private waitForRecaptcha(): void {
    const checkInterval = setInterval(() => {
      if (typeof grecaptcha !== 'undefined' && grecaptcha.render) {
        clearInterval(checkInterval);
        this.scriptLoaded = true;
        if (this.recaptchaContainer) {
          this.renderRecaptcha();
        }
      }
    }, 100);

    setTimeout(() => {
      clearInterval(checkInterval);
      if (!this.scriptLoaded) {
        console.error('Timeout ao aguardar reCAPTCHA');
        this.error.emit('Timeout ao carregar reCAPTCHA');
      }
    }, 10000);
  }

  private renderRecaptcha(): void {
    if (!this.recaptchaContainer || typeof grecaptcha === 'undefined') {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      try {
        this.recaptchaWidgetId = grecaptcha.render(this.recaptchaContainer.nativeElement, {
          sitekey: this.recaptchaSiteKey,
          theme: this.theme,
          size: this.size,
          callback: (token: string) => {
            this.ngZone.run(() => {
              this.onRecaptchaVerify(token);
            });
          },
          'expired-callback': () => {
            this.ngZone.run(() => {
              this.onRecaptchaExpired(null);
            });
          },
          'error-callback': (err: any) => {
            this.ngZone.run(() => {
              this.onRecaptchaError(err);
            });
          }
        });
      } catch (e) {
        console.error('Erro ao renderizar reCAPTCHA:', e);
        this.error.emit(e);
      }
    });
  }

  private loadHcaptchaScript(): void {
    if (typeof hcaptcha !== 'undefined') {
      this.scriptLoaded = true;
      return;
    }

    if (document.querySelector('script[src*="hcaptcha.com/1/api.js"]')) {
      this.waitForHcaptcha();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.hcaptcha.com/1/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.scriptLoaded = true;
      if (this.hcaptchaContainer) {
        this.renderHcaptcha();
      }
    };
    script.onerror = () => {
      console.error('Falha ao carregar o script do hCaptcha');
      this.error.emit('Falha ao carregar hCaptcha');
    };
    document.head.appendChild(script);
  }

  private waitForHcaptcha(): void {
    const checkInterval = setInterval(() => {
      if (typeof hcaptcha !== 'undefined') {
        clearInterval(checkInterval);
        this.scriptLoaded = true;
        if (this.hcaptchaContainer) {
          this.renderHcaptcha();
        }
      }
    }, 100);

    setTimeout(() => {
      clearInterval(checkInterval);
      if (!this.scriptLoaded) {
        console.error('Timeout ao aguardar hCaptcha');
        this.error.emit('Timeout ao carregar hCaptcha');
      }
    }, 10000);
  }

  private renderHcaptcha(): void {
    if (!this.hcaptchaContainer || typeof hcaptcha === 'undefined') {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      try {
        this.hcaptchaWidgetId = hcaptcha.render(this.hcaptchaContainer.nativeElement, {
          sitekey: this.hcaptchaSiteKey,
          theme: this.theme,
          size: this.size,
          callback: (token: string) => {
            this.ngZone.run(() => {
              this.onHcaptchaVerify(token);
            });
          },
          'expired-callback': () => {
            this.ngZone.run(() => {
              this.onHcaptchaExpired(null);
            });
          },
          'error-callback': (err: any) => {
            this.ngZone.run(() => {
              this.onHcaptchaError(err);
            });
          }
        });
      } catch (e) {
        console.error('Erro ao renderizar hCaptcha:', e);
        this.error.emit(e);
      }
    });
  }

  writeValue(value: any): void {
    this.token = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onRecaptchaVerify(token: string): void {
    this.token = token;
    this.onChange(token);
    this.verify.emit(token);
  }

  onRecaptchaExpired(response: any): void {
    this.token = undefined;
    this.onChange(null);
    this.expired.emit(response);
  }

  onRecaptchaError(error: any): void {
    this.token = undefined;
    this.onChange(null);
    this.error.emit(error);
  }

  onHcaptchaVerify(token: string): void {
    this.token = token;
    this.onChange(token);
    this.verify.emit(token);
  }

  onHcaptchaExpired(response: any): void {
    this.token = undefined;
    this.onChange(null);
    this.expired.emit(response);
  }

  onHcaptchaError(error: any): void {
    this.token = undefined;
    this.onChange(null);
    this.error.emit(error);
  }

  onTurnstileVerify(token: string): void {
    this.token = token;
    this.onChange(token);
    this.verify.emit(token);
  }

  onTurnstileExpired(response: any): void {
    this.token = undefined;
    this.onChange(null);
    this.expired.emit(response);
  }

  onTurnstileError(error: any): void {
    this.token = undefined;
    this.onChange(null);
    this.error.emit(error);
  }

  private loadTurnstileScript(): void {
    if (typeof turnstile !== 'undefined') {
      this.scriptLoaded = true;
      return;
    }

    if (document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')) {
      this.waitForTurnstile();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.scriptLoaded = true;
      if (this.turnstileContainer) {
        this.renderTurnstile();
      }
    };
    script.onerror = () => {
      console.error('Falha ao carregar o script do Turnstile');
      this.error.emit('Falha ao carregar Turnstile');
    };
    document.head.appendChild(script);
  }

  private waitForTurnstile(): void {
    const checkInterval = setInterval(() => {
      if (typeof turnstile !== 'undefined') {
        clearInterval(checkInterval);
        this.scriptLoaded = true;
        if (this.turnstileContainer) {
          this.renderTurnstile();
        }
      }
    }, 100);

    setTimeout(() => {
      clearInterval(checkInterval);
      if (!this.scriptLoaded) {
        console.error('Timeout ao aguardar Turnstile');
        this.error.emit('Timeout ao carregar Turnstile');
      }
    }, 10000);
  }

  private renderTurnstile(): void {
    if (!this.turnstileContainer || typeof turnstile === 'undefined') {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      try {
        this.turnstileWidgetId = turnstile.render(this.turnstileContainer.nativeElement, {
          sitekey: this.turnstileSiteKey,
          theme: this.theme,
          size: this.size,
          callback: (token: string) => {
            this.ngZone.run(() => {
              this.onTurnstileVerify(token);
            });
          },
          'expired-callback': () => {
            this.ngZone.run(() => {
              this.onTurnstileExpired(null);
            });
          },
          'error-callback': (err: any) => {
            this.ngZone.run(() => {
              this.onTurnstileError(err);
            });
          }
        });
      } catch (e) {
        console.error('Erro ao renderizar Turnstile:', e);
        this.error.emit(e);
      }
    });
  }

  isRecaptcha(): boolean {
    return this.captchaType === 'recaptcha';
  }

  isHcaptcha(): boolean {
    return this.captchaType === 'hcaptcha';
  }

  isTurnstile(): boolean {
    return this.captchaType === 'turnstile';
  }

  reset(): void {
    if (this.captchaType === 'recaptcha' && this.recaptchaWidgetId !== null && typeof grecaptcha !== 'undefined') {
      try {
        grecaptcha.reset(this.recaptchaWidgetId);
      } catch (e) {
        console.error('Erro ao resetar reCAPTCHA:', e);
      }
    } else if (this.captchaType === 'hcaptcha' && this.hcaptchaWidgetId && typeof hcaptcha !== 'undefined') {
      try {
        hcaptcha.reset(this.hcaptchaWidgetId);
      } catch (e) {
        console.error('Erro ao resetar hCaptcha:', e);
      }
    } else if (this.captchaType === 'turnstile' && this.turnstileWidgetId && typeof turnstile !== 'undefined') {
      try {
        turnstile.reset(this.turnstileWidgetId);
      } catch (e) {
        console.error('Erro ao resetar Turnstile:', e);
      }
    }
    this.token = undefined;
    this.onChange(null);
  }
}

